import React, { useCallback, useEffect, useState } from "react";
import { FiRefreshCw, FiRotateCcw, FiDollarSign, FiAlertCircle } from "react-icons/fi";

import axiosInstance from "../config/axiosInstance";
import NoData from "../components/NoData";
import {
  adminGetSummary,
  adminListOrders,
  adminListWebhooks,
  adminRetryWebhooks,
  adminRefund,
  formatBRL,
  formatDate,
  formatDateTime,
  getErrorMessage,
  ORDER_STATUS_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
} from "../services/billingApi";
import "./AdminAssinaturasPage.css";

const TABS = [
  { id: "assinaturas", label: "Assinaturas" },
  { id: "pedidos", label: "Pedidos" },
  { id: "webhooks", label: "Webhooks" },
];

const WEBHOOK_TONES = {
  PROCESSED: "success",
  RECEIVED: "info",
  IGNORED: "info",
  FAILED: "danger",
};

const AdminAssinaturasPage = () => {
  const [tab, setTab] = useState("assinaturas");
  const [summary, setSummary] = useState(null);
  const [assinaturas, setAssinaturas] = useState([]);
  const [orders, setOrders] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Cada bloco falha isoladamente — um erro no resumo não some com a lista.
      const [summaryResult, subsResult, ordersResult, webhooksResult] =
        await Promise.allSettled([
          adminGetSummary(),
          axiosInstance.get("/subscriptions"),
          adminListOrders(),
          adminListWebhooks(),
        ]);

      if (summaryResult.status === "fulfilled") setSummary(summaryResult.value);
      if (subsResult.status === "fulfilled") {
        setAssinaturas(subsResult.value?.data?.data || []);
      }
      if (ordersResult.status === "fulfilled") setOrders(ordersResult.value || []);
      if (webhooksResult.status === "fulfilled") setWebhooks(webhooksResult.value || []);

      setError(
        summaryResult.status === "rejected"
          ? getErrorMessage(summaryResult.reason, "Erro ao carregar o resumo financeiro")
          : null
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetryWebhooks = async () => {
    setBusy(true);
    try {
      const result = await adminRetryWebhooks();
      setNotice(`${result?.retried || 0} evento(s) reprocessado(s).`);
      await load();
    } catch (retryError) {
      setNotice(getErrorMessage(retryError, "Não foi possível reprocessar."));
    } finally {
      setBusy(false);
    }
  };

  const handleRefund = async (order) => {
    if (!order.mpPaymentId) return;
    if (!window.confirm(`Estornar ${formatBRL(order.amountCents)} de "${order.description}"?`)) {
      return;
    }

    setBusy(true);
    try {
      await adminRefund(order.mpPaymentId);
      setNotice("Estorno solicitado ao Mercado Pago.");
      await load();
    } catch (refundError) {
      setNotice(getErrorMessage(refundError, "Não foi possível estornar."));
    } finally {
      setBusy(false);
    }
  };

  const failedWebhooks = webhooks.filter((event) => event.status === "FAILED").length;

  return (
    <div className="fade-in page-stack admin-billing">
      <div className="page-header">
        <div>
          <h2>Receita e assinaturas</h2>
          <p>Acompanhe MRR, pedidos e a saúde da integração com o Mercado Pago.</p>
        </div>
        <button type="button" className="button button--ghost" onClick={load} disabled={loading}>
          <FiRefreshCw /> Atualizar
        </button>
      </div>

      {error && <div className="admin-billing__error">{error}</div>}
      {notice && <div className="admin-billing__notice">{notice}</div>}

      {/* ── Indicadores ────────────────────────────────────────────────── */}
      {summary && (
        <div className="admin-billing__kpis">
          <div className="kpi">
            <span className="kpi__label">MRR</span>
            <strong className="kpi__value">{formatBRL(summary.mrrCents)}</strong>
            <small>ARR {formatBRL(summary.arrCents)}</small>
          </div>
          <div className="kpi">
            <span className="kpi__label">Recebido (bruto)</span>
            <strong className="kpi__value">{formatBRL(summary.totalGrossCents)}</strong>
            <small>Líquido {formatBRL(summary.totalNetCents)}</small>
          </div>
          <div className="kpi">
            <span className="kpi__label">Taxas do gateway</span>
            <strong className="kpi__value">{formatBRL(summary.gatewayFeesCents)}</strong>
            <small>
              {summary.totalGrossCents > 0
                ? `${((summary.gatewayFeesCents / summary.totalGrossCents) * 100).toFixed(1)}% do bruto`
                : "—"}
            </small>
          </div>
          <div className="kpi">
            <span className="kpi__label">Assinaturas ativas</span>
            <strong className="kpi__value">{summary.activeSubscriptions}</strong>
            <small>{summary.activeEntitlements} add-on(s) ativos</small>
          </div>
          <div className={`kpi${summary.delinquentSubscriptions > 0 ? " kpi--alert" : ""}`}>
            <span className="kpi__label">Inadimplentes</span>
            <strong className="kpi__value">{summary.delinquentSubscriptions}</strong>
            <small>cobranças recusadas</small>
          </div>
        </div>
      )}

      {/* Receita por meio de pagamento — mostra se PIX ou cartão domina. */}
      {summary?.revenueByPaymentType &&
        Object.keys(summary.revenueByPaymentType).length > 0 && (
          <div className="section-card">
            <h3 className="card-title">
              <FiDollarSign /> Receita por meio de pagamento
            </h3>
            <div className="admin-billing__methods">
              {Object.entries(summary.revenueByPaymentType).map(([type, cents]) => {
                const percent = summary.totalGrossCents
                  ? Math.round((cents / summary.totalGrossCents) * 100)
                  : 0;
                return (
                  <div key={type} className="method-bar">
                    <div className="method-bar__head">
                      <span>{type}</span>
                      <strong>{formatBRL(cents)}</strong>
                    </div>
                    <div className="method-bar__track">
                      <span style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* ── Abas ───────────────────────────────────────────────────────── */}
      <div className="admin-billing__tabs">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`admin-billing__tab${tab === item.id ? " admin-billing__tab--active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
            {item.id === "webhooks" && failedWebhooks > 0 && (
              <span className="admin-billing__tab-badge">{failedWebhooks}</span>
            )}
          </button>
        ))}
      </div>

      <div className="section-card">
        {loading ? (
          <div className="muted">Carregando...</div>
        ) : tab === "assinaturas" ? (
          assinaturas.length === 0 ? (
            <NoData
              title="Nenhuma assinatura encontrada"
              description="Nenhum anunciante realizou assinatura ainda."
            />
          ) : (
            <div className="admin-billing__table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Plano</th>
                    <th>Limite</th>
                    <th>Preço</th>
                    <th>Renovação</th>
                    <th>Status</th>
                    <th>Expira em</th>
                  </tr>
                </thead>
                <tbody>
                  {assinaturas.map((item) => {
                    const statusInfo =
                      SUBSCRIPTION_STATUS_LABELS[item.status] || {
                        label: item.status,
                        tone: "info",
                      };
                    return (
                      <tr key={item.id}>
                        <td>{item.company?.nomeFantasia || item.companyId}</td>
                        <td>{item.plan}</td>
                        <td>{item.productsLimit < 0 ? "ilimitado" : item.productsLimit}</td>
                        <td>{formatBRL(item.priceCents ?? Number(item.price) * 100)}</td>
                        <td>{item.autoRenew ? "Automática" : "Manual"}</td>
                        <td>
                          <span className="badge" data-tone={statusInfo.tone}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td>{formatDate(item.expiresAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : tab === "pedidos" ? (
          orders.length === 0 ? (
            <NoData title="Nenhum pedido" description="Nenhuma cobrança foi gerada ainda." />
          ) : (
            <div className="admin-billing__table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Empresa</th>
                    <th>Item</th>
                    <th>Valor</th>
                    <th>Método</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const statusInfo = ORDER_STATUS_LABELS[order.status] || {
                      label: order.status,
                      tone: "info",
                    };
                    return (
                      <tr key={order.id}>
                        <td>{formatDateTime(order.createdAt)}</td>
                        <td>{order.company?.nomeFantasia || "—"}</td>
                        <td>{order.description}</td>
                        <td>{formatBRL(order.amountCents)}</td>
                        <td>{order.paymentMethod || "—"}</td>
                        <td>
                          <span className="badge" data-tone={statusInfo.tone}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td>
                          {order.status === "PAID" && order.mpPaymentId && (
                            <button
                              type="button"
                              className="admin-billing__mini-btn"
                              onClick={() => handleRefund(order)}
                              disabled={busy}
                            >
                              <FiRotateCcw /> Estornar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <>
            <div className="admin-billing__webhook-head">
              <p className="muted">
                Cada notificação do Mercado Pago é registrada aqui. Eventos com falha
                são reprocessados automaticamente a cada 30 minutos.
              </p>
              <button
                type="button"
                className="button button--ghost"
                onClick={handleRetryWebhooks}
                disabled={busy || failedWebhooks === 0}
              >
                <FiRotateCcw /> Reprocessar falhas
              </button>
            </div>

            {webhooks.length === 0 ? (
              <NoData
                title="Nenhum evento recebido"
                description="Configure a URL de webhook no painel do Mercado Pago para começar a receber notificações."
              />
            ) : (
              <div className="admin-billing__table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Tópico</th>
                      <th>Recurso</th>
                      <th>Assinatura</th>
                      <th>Tentativas</th>
                      <th>Status</th>
                      <th>Detalhe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhooks.map((event) => (
                      <tr key={event.id}>
                        <td>{formatDateTime(event.createdAt)}</td>
                        <td>{event.topic}</td>
                        <td className="admin-billing__mono">{event.resourceId}</td>
                        <td>
                          {event.signatureValid ? (
                            "validada"
                          ) : (
                            <span className="admin-billing__warn">
                              <FiAlertCircle /> sem segredo
                            </span>
                          )}
                        </td>
                        <td>{event.attempts}</td>
                        <td>
                          <span className="badge" data-tone={WEBHOOK_TONES[event.status] || "info"}>
                            {event.status}
                          </span>
                        </td>
                        <td className="admin-billing__detail">{event.error || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAssinaturasPage;
